import type {
  Confidence,
  EventType,
  DayType,
  PkType,
  ProductMixEntry,
  Provenance,
} from '@/domain';
import {
  aggregateHistory,
  resolveBaseline,
  type HistoricalSample,
} from './historical-aggregator';
import { generate } from './generator';
import {
  computeTotalWorkload,
  computeWlu,
  uphLookupFromMap,
} from '../wlu/calculator';

/**
 * Pure forecast orchestrator. Caller resolves all inputs (history, configs)
 * before calling — engine never touches I/O.
 */

export interface ForecastInput {
  warehouse_id: string;
  customer_id: string;
  target_date: string; // YYYY-MM-DD
  target_event_type: EventType;
  target_day_type: DayType;

  history: HistoricalSample[]; // already filtered to this warehouse+customer
  history_window_start: string;
  history_window_end: string;

  monthly_multipliers: Record<number, number>; // {1..12: factor}
  event_multipliers: Record<EventType, number>;
  uph_norms: Map<string, number>; // key: "uph:wh:customer:pg:pk" → units/hour
  product_mix: ProductMixEntry[];
  pk_type?: PkType;

  config_versions?: Record<string, number>;
}

export interface ForecastOutput {
  warehouse_id: string;
  customer_id: string;
  date: string;
  event_type: EventType;
  day_type: DayType;
  forecast_qty: number;
  wlu_per_order_minutes: number;
  uph_eff: number;
  total_workload_minutes: number;
  total_workload_hours: number;
  provenance: Provenance;
  confidence: Confidence;
}

const LOW_CONFIDENCE_THRESHOLD = 5;

export function runForecast(input: ForecastInput): ForecastOutput {
  const month = Number(input.target_date.slice(5, 7));
  const monthMul = input.monthly_multipliers[month] ?? 1.0;
  const eventMul = input.event_multipliers[input.target_event_type] ?? 1.0;

  // 1. Aggregate history → bucket by event_type
  const aggregated = aggregateHistory(input.history);

  // 2. Resolve baseline with fallback chain
  const baseline = resolveBaseline(aggregated, input.target_event_type);

  // 3. Apply multipliers
  const gen = generate({
    baseline,
    monthly_multiplier: monthMul,
    event_multiplier: eventMul,
  });

  // 4. Compute WLU + workload
  const wluResult = computeWlu({
    product_mix: input.product_mix,
    uph: uphLookupFromMap(input.uph_norms, input.warehouse_id, input.customer_id),
    pk_type: input.pk_type,
  });
  const workload = computeTotalWorkload({
    wlu_per_order_minutes: wluResult.wlu_per_order_minutes,
    forecast_qty: gen.forecast_qty,
  });

  const provenance: Provenance = {
    historical_window_start: input.history_window_start,
    historical_window_end: input.history_window_end,
    historical_sample_size: aggregated.totalRows,
    historical_baseline_avg: baseline.stats.avg,
    historical_baseline_p85: baseline.stats.p85 > 0 ? baseline.stats.p85 : null,
    baseline_event_type: baseline.resolved_type,
    fallback_used: baseline.fallback_used,
    monthly_multiplier: monthMul,
    event_multiplier: eventMul,
    config_versions: input.config_versions ?? {},
    uph_eff: wluResult.uph_eff,
  };

  const confidence: Confidence = {
    p85: gen.p85,
    p50: baseline.stats.p50 > 0 ? baseline.stats.p50 * monthMul * eventMul : null,
    sample_size: baseline.stats.n,
    is_low_confidence:
      baseline.stats.n < LOW_CONFIDENCE_THRESHOLD || baseline.fallback_used,
  };

  return {
    warehouse_id: input.warehouse_id,
    customer_id: input.customer_id,
    date: input.target_date,
    event_type: input.target_event_type,
    day_type: input.target_day_type,
    forecast_qty: gen.forecast_qty,
    wlu_per_order_minutes: wluResult.wlu_per_order_minutes,
    uph_eff: wluResult.uph_eff,
    total_workload_minutes: workload.total_workload_minutes,
    total_workload_hours: workload.total_workload_hours,
    provenance,
    confidence,
  };
}

export { aggregateHistory, resolveBaseline } from './historical-aggregator';
export { generate } from './generator';
